import { TrashIcon } from '@phosphor-icons/react';
import { useMemo, useRef, type JSX } from 'react';
import {
  Controller,
  useFieldArray,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormSetValue,
} from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { slugifyKey } from '@/lib/utils/slugify-key';
/** Mutable form-shape mirror of CustomField (RHF requires mutable arrays). */
export interface CustomFieldFormRow {
  key: string;
  label: string;
  dataType: 'text' | 'number' | 'date' | 'dropdown';
  required: boolean;
  dropdownOptions: string[] | null;
}

/**
 * Plan 10-02 D-95 — CustomFieldsBuilder
 *
 * Dynamic-row schema builder bound via React Hook Form `useFieldArray`. Lets
 * the user add / remove / reorder schema rows. Each row contains:
 *   - Label input (drives auto-suggested Key)
 *   - Key input (auto-suggested from Label until user manually overrides)
 *   - DataType select (text|number|date|dropdown)
 *   - Required checkbox
 *   - DropdownOptions textarea (only when DataType === 'dropdown')
 *   - Three buttons: move-up, move-down, remove
 *
 * D-93 client-side mirror — backend re-validates via CustomFieldValidator
 * (Plan 10-01). Inline duplicate-key detection runs in-component because Zod's
 * superRefine fires only on submit; we surface the error per row immediately.
 */

const KEY_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/** The form-shape this builder writes to. The parent form must declare a
 * `customFields: CustomField[]` field. The builder is generic over any
 * containing form type that has that field via the `<TName>` slot. */
export interface CustomFieldsBuilderProps {
  readonly control: Control<{ customFields: CustomFieldFormRow[] }>;
  /** Optional cross-row errors surfaced from the parent form (e.g. duplicate
   * keys from Zod superRefine). */
  readonly errors?: FieldErrors<{ customFields: CustomFieldFormRow[] }>;
  /** Optional setValue used to auto-suggest the Key field from the Label
   * field. When undefined, auto-suggest is disabled (the user must enter Key
   * manually). */
  readonly setValue?: UseFormSetValue<{ customFields: CustomFieldFormRow[] }>;
  readonly disabled?: boolean;
}

const DATA_TYPE_OPTIONS: readonly { value: CustomFieldFormRow['dataType']; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
];

export function CustomFieldsBuilder({
  control,
  errors,
  setValue,
  disabled = false,
}: CustomFieldsBuilderProps): JSX.Element {
  const { fields, append, remove, move } = useFieldArray<
    { customFields: CustomFieldFormRow[] },
    'customFields'
  >({
    control,
    name: 'customFields',
  });

  // Track per-row whether the user has manually edited the Key — once true we
  // stop auto-suggesting from Label changes for that row.
  const keyDirtyRef = useRef<Map<string, boolean>>(new Map());

  // Subscribe to the live array values so we can compute duplicate-key warnings
  // *before* the user submits the form (Zod's superRefine only fires on
  // submit). The value is also used to drive the dropdownOptions textarea.
  const watched = useWatch({ control, name: 'customFields' }) as CustomFieldFormRow[] | undefined;

  // Compute which keys are duplicated across rows — used for inline error
  // surfacing on every offending row.
  const duplicateKeys = useMemo<Set<string>>(() => {
    const counts = new Map<string, number>();
    for (const f of watched ?? []) {
      if (f.key.length > 0) counts.set(f.key, (counts.get(f.key) ?? 0) + 1);
    }
    const dupes = new Set<string>();
    for (const [k, n] of counts) if (n > 1) dupes.add(k);
    return dupes;
  }, [watched]);

  function handleAddRow(): void {
    append({
      key: '',
      label: '',
      dataType: 'text',
      required: false,
      dropdownOptions: null,
    });
  }

  function optionsToText(opts: string[] | null): string {
    if (opts === null) return '';
    return opts.join('\n');
  }

  function textToOptions(text: string): string[] {
    return text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  return (
    <div className="space-y-4" data-testid="custom-fields-builder">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Custom fields</Label>
          <p className="text-xs text-muted-foreground">
            Define the schema rows that machines of this type will fill in.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddRow}
          disabled={disabled}
          data-testid="cfb-add-row"
        >
          + Add row
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="cfb-empty">
          No custom fields yet. Add a row to define the schema.
        </p>
      ) : (
        <ul className="space-y-3">
          {fields.map((rowField, index) => {
            const liveRow: CustomFieldFormRow | undefined = watched?.[index];
            const dataType: CustomFieldFormRow['dataType'] = liveRow?.dataType ?? 'text';
            const liveKey = liveRow?.key ?? '';
            const isDuplicate = liveKey.length > 0 && duplicateKeys.has(liveKey);
            const isInvalidKey = liveKey.length > 0 && !KEY_REGEX.test(liveKey);
            const rowErrors = errors?.customFields?.[index];

            return (
              <li
                key={rowField.id}
                className="rounded-md border border-input bg-card p-3"
                data-testid={`cfb-row-${String(index)}`}
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Label */}
                  <div className="space-y-1">
                    <Label htmlFor={`cfb-${String(index)}-label`}>Label</Label>
                    <Controller
                      control={control}
                      name={`customFields.${index}.label`}
                      render={({ field }) => (
                        <Input
                          id={`cfb-${String(index)}-label`}
                          type="text"
                          autoComplete="off"
                          disabled={disabled}
                          value={typeof field.value === 'string' ? field.value : ''}
                          onChange={(e) => {
                            const next = e.target.value;
                            field.onChange(next);
                            // Auto-suggest Key from Label unless the user has
                            // manually edited the Key for this row. setValue
                            // updates the Key field's controlled value
                            // immediately so the Controller re-renders.
                            const dirty = keyDirtyRef.current.get(rowField.id) === true;
                            if (!dirty && setValue) {
                              const suggested = slugifyKey(next);
                              setValue(`customFields.${index}.key`, suggested, {
                                shouldDirty: true,
                                shouldValidate: false,
                              });
                            }
                          }}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          data-testid={`cfb-row-${String(index)}-label`}
                        />
                      )}
                    />
                    {rowErrors?.label?.message !== undefined && (
                      <p
                        className="text-xs text-destructive"
                        data-testid={`cfb-row-${String(index)}-label-error`}
                      >
                        {rowErrors.label.message}
                      </p>
                    )}
                  </div>

                  {/* Key */}
                  <div className="space-y-1">
                    <Label htmlFor={`cfb-${String(index)}-key`}>Key</Label>
                    <Controller
                      control={control}
                      name={`customFields.${index}.key`}
                      render={({ field }) => (
                        <Input
                          id={`cfb-${String(index)}-key`}
                          type="text"
                          autoComplete="off"
                          disabled={disabled}
                          value={typeof field.value === 'string' ? field.value : ''}
                          onChange={(e) => {
                            keyDirtyRef.current.set(rowField.id, true);
                            field.onChange(e.target.value);
                          }}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          aria-invalid={isDuplicate || isInvalidKey}
                          data-testid={`cfb-row-${String(index)}-key`}
                        />
                      )}
                    />
                    {isDuplicate && (
                      <p
                        className="text-xs text-destructive"
                        data-testid={`cfb-row-${String(index)}-key-duplicate-error`}
                      >
                        Key &apos;{liveKey}&apos; is duplicated
                      </p>
                    )}
                    {isInvalidKey && !isDuplicate && (
                      <p
                        className="text-xs text-destructive"
                        data-testid={`cfb-row-${String(index)}-key-format-error`}
                      >
                        Key must start with a letter; only letters, digits, and underscores allowed.
                      </p>
                    )}
                    {rowErrors?.key?.message !== undefined && !isDuplicate && !isInvalidKey && (
                      <p
                        className="text-xs text-destructive"
                        data-testid={`cfb-row-${String(index)}-key-error`}
                      >
                        {rowErrors.key.message}
                      </p>
                    )}
                  </div>

                  {/* DataType */}
                  <div className="space-y-1">
                    <Label htmlFor={`cfb-${String(index)}-datatype`}>Data type</Label>
                    <Controller
                      control={control}
                      name={`customFields.${index}.dataType`}
                      render={({ field }) => (
                        <Select
                          value={typeof field.value === 'string' ? field.value : 'text'}
                          onValueChange={(v) => {
                            const nextType = v as CustomFieldFormRow['dataType'];
                            field.onChange(nextType);
                            // Sync dropdownOptions: when switching TO dropdown,
                            // initialize with [''] if currently null. When
                            // switching AWAY, set to null.
                            if (setValue) {
                              const current = watched?.[index];
                              const nextOptions: string[] | null =
                                nextType === 'dropdown' ? (current?.dropdownOptions ?? ['']) : null;
                              setValue(`customFields.${index}.dropdownOptions`, nextOptions, {
                                shouldDirty: true,
                                shouldValidate: false,
                              });
                            }
                          }}
                          disabled={disabled}
                        >
                          <SelectTrigger
                            id={`cfb-${String(index)}-datatype`}
                            data-testid={`cfb-row-${String(index)}-datatype`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DATA_TYPE_OPTIONS.map((opt) => (
                              <SelectItem
                                key={opt.value}
                                value={opt.value}
                                data-testid={`cfb-row-${String(index)}-datatype-${opt.value}`}
                              >
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {/* Required checkbox */}
                  <div className="flex items-center gap-2 pt-6">
                    <Controller
                      control={control}
                      name={`customFields.${index}.required`}
                      render={({ field }) => (
                        <Checkbox
                          id={`cfb-${String(index)}-required`}
                          checked={field.value}
                          onCheckedChange={(v) => {
                            field.onChange(v === true);
                          }}
                          disabled={disabled}
                          data-testid={`cfb-row-${String(index)}-required`}
                        />
                      )}
                    />
                    <Label htmlFor={`cfb-${String(index)}-required`} className="text-sm">
                      Required
                    </Label>
                  </div>
                </div>

                {dataType === 'dropdown' && (
                  <div className="mt-3 space-y-1">
                    <Label htmlFor={`cfb-${String(index)}-dropdown-options`}>
                      Dropdown options (one per line)
                    </Label>
                    <Controller
                      control={control}
                      name={`customFields.${index}.dropdownOptions`}
                      render={({ field }) => (
                        <Textarea
                          id={`cfb-${String(index)}-dropdown-options`}
                          rows={3}
                          disabled={disabled}
                          value={optionsToText(Array.isArray(field.value) ? field.value : null)}
                          onChange={(e) => {
                            const opts = textToOptions(e.target.value);
                            field.onChange(opts);
                          }}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          placeholder="Option 1&#10;Option 2"
                          data-testid={`cfb-row-${String(index)}-dropdown-options`}
                        />
                      )}
                    />
                    {rowErrors?.dropdownOptions?.message !== undefined && (
                      <p
                        className="text-xs text-destructive"
                        data-testid={`cfb-row-${String(index)}-dropdown-options-error`}
                      >
                        {rowErrors.dropdownOptions.message}
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-3 flex items-center justify-end gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled || index === 0}
                    onClick={() => {
                      move(index, index - 1);
                    }}
                    data-testid={`cfb-row-${String(index)}-up`}
                    aria-label="Move row up"
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled || index === fields.length - 1}
                    onClick={() => {
                      move(index, index + 1);
                    }}
                    data-testid={`cfb-row-${String(index)}-down`}
                    aria-label="Move row down"
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => {
                      keyDirtyRef.current.delete(rowField.id);
                      remove(index);
                    }}
                    data-testid={`cfb-row-${String(index)}-remove`}
                    aria-label="Remove row"
                  >
                    <TrashIcon size={14} />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
